import { useCallback } from 'react'

import { sectionsBatiKal, type Sections } from '@batical/calculPage/src/SectionCalculList'
import { setSection } from '@batical/reducer/src'
import { Box, Container, Paper, Typography, useTheme } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import Page from 'src/components/Page'

import SectionCard from './SectionCard'

const SectionsList = () => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const goToCalculList = useCallback(
    (code: string) => {
      dispatch(setSection(code))
      navigate('/dashboard/calculs-par-domaine')
    },
    [dispatch, navigate]
  )

  return (
    <Page title="BatiKal | Calculs par domaine">
      <Box sx={{ bgcolor: '#ffffff', minHeight: '100%', pb: { xs: 3, md: 5 } }}>
        <Container maxWidth="lg" sx={{ pt: { xs: 2, md: 2.5 }, pb: 0 }}>
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              mb: { xs: 2.5, md: 3 },
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
              background: `linear-gradient(100deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.primary.main, 0.075)} 34%, ${alpha(theme.palette.primary.main, 0.03)} 55%, ${theme.palette.background.paper} 85%)`,
              overflow: 'hidden'
            }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { md: 'center' },
                justifyContent: 'space-between',
                gap: { xs: 1.25, md: 2 },
                px: { xs: 2, sm: 2.5, md: 3 },
                py: { xs: 1.35, md: 1.5 },
                textAlign: 'left'
              }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 1.5, sm: 2 },
                  minWidth: 0,
                  flex: { md: '1 1 auto' }
                }}>
                <Box
                  sx={{
                    width: 4,
                    flexShrink: 0,
                    alignSelf: 'stretch',
                    minHeight: { xs: 40, md: 52 },
                    borderRadius: 1,
                    bgcolor: 'primary.main'
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    component="p"
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: '0.62rem', sm: '0.68rem' },
                      letterSpacing: '0.18em',
                      textTransform: 'uppercase',
                      color: 'primary.dark',
                      mb: 0.35,
                      opacity: 0.95
                    }}>
                    Au cœur de BatiKal — vos calculs
                  </Typography>
                  <Typography
                    component="h1"
                    sx={{
                      fontWeight: 900,
                      color: '#0f172a',
                      letterSpacing: '-0.03em',
                      lineHeight: 1.08,
                      fontSize: { xs: '1.65rem', sm: '1.95rem', md: '2.15rem' },
                      wordBreak: 'break-word'
                    }}>
                    Sections
                  </Typography>
                </Box>
              </Box>
              <Typography
                component="p"
                sx={{
                  color: '#334155',
                  fontSize: { xs: '0.9rem', sm: '0.95rem' },
                  lineHeight: 1.55,
                  fontWeight: 500,
                  maxWidth: { md: 540 },
                  flex: { md: '1 1 44%' },
                  m: 0,
                  pl: { xs: 0.25, md: 0 }
                }}>
                Accès aux <Box component="strong" sx={{ color: '#0f172a', fontWeight: 700 }}>calculs</Box> par domaine
                (gaz, ECS, chaufferie…). Choisissez une section pour voir les outils et en lancer un.
              </Typography>
            </Box>
          </Paper>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, minmax(0, 1fr))',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(3, minmax(0, 1fr))',
                lg: 'repeat(4, minmax(0, 1fr))'
              },
              gap: { xs: '14px', sm: '16px', md: '18px' },
              alignItems: 'stretch'
            }}>
            {sectionsBatiKal.map((tile: Sections) => (
              <SectionCard key={`sectionlist-key-${tile}`} code={tile} onClick={goToCalculList} />
            ))}
          </Box>
        </Container>
      </Box>
    </Page>
  )
}

export default SectionsList
